import React, { useEffect, useState, useContext } from "react";
import { Grid, Box } from "@mui/material";
import { getAboutUsById, updateAboutUs } from "Apis/aboutUs";
import { useFormik } from "formik";
import MDInput from "components/MDInput";
import ImageUploader from "components/MDImageUploader";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { LoginFormContext } from "context/LoginFormContext";
import TableContainer from "@mui/material/TableContainer";
import MDTextField from "components/MDTextField";
import MDGoogleMap from "components/MDGoogleMap";
import MDTelInput from "components/MDTelInput";
import { initialValues } from "./Schema/initialValue";
import { validationSchema } from "./Schema/validation";
import SubmitButton from "components/SubmitButton";
import colors from "assets/theme/base/colors";
import MDBox from "components/MDBox";
import TinyTextEditor from "components/MDTinyTextEditor";

const AboutUs = () => {
  const { setNotification, profileImage, setProfileImage, setProfileName, setProfile } =
    useContext(LoginFormContext);
  const [aboutUsId, setAboutUsId] = useState(0);
  const [selectedPosition, setSelectedPosition] = useState();
  const [mobileNumber, setMobileNumber] = useState("+91");
  const [officeNumber, setOfficeNumber] = useState("+91");
  const { sideNav } = colors;
  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        values.id = aboutUsId;
        const response = await updateAboutUs(values);
        if (response.status === 200) {
          setNotification({
            color: "success",
            icon: "check",
            title: "Success",
            content: response?.data?.message,
            open: true,
          });
          getAboutUsData();
        }
      } catch (error) {
        console.error("Error updating About Us data:", error);
      }
    },
  });

  const getAboutUsData = async () => {
    let response = await getAboutUsById();
    if (response?.status === 200) {
      const data = response?.data?.data?.result;
      setAboutUsId(data.id);
      formik.setValues({
        name: data.name || "",
        email: data.email || "",
        mobile_number: data.mobile_number || "",
        description: data.description || "",
        privacy_policy: data.privacy_policy || "",
        terms_and_condition: data.terms_and_condition || "",
        address: data.address || "",
        logo: data.logo || "",
        information_images: data.information_images || "",
        announcement: data.announcement || "",
        instagram_link: data.instagram_link || "",
        youtube_link: data.youtube_link || "",
        facebook_link: data.facebook_link || "",
        twitter_link: data.twitter_link || "",
        country_code: data.country_code || "",
        latitude: data.latitude || "",
        longitude: data.longitude || "",
      });
      setMobileNumber("+" + data?.country_code + data?.mobile_number?.toString());
      setOfficeNumber("+" + data?.country_code + data?.office_number?.toString());
      setProfileImage({
        url: data?.information_images?.[0]?.image || "",
        name: null,
      });
      setProfile({
        url: data?.information_images?.[0]?.image || "",
        name: null,
      });

      setProfileName(data?.name);
      if (!data?.latitude || !data?.longitude) {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition((position) => {
            const userLocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            setSelectedPosition(userLocation);
            formik?.setFieldValue("latitude", userLocation.lat);
            formik?.setFieldValue("longitude", userLocation.lng);
          });
        }
      } else {
        const lat = data.latitude;
        const lng = data?.longitude;
        setSelectedPosition({ lat, lng });
      }
    }
  };

  useEffect(() => {
    getAboutUsData();
  }, []);

  const handlePhoneValueChange = (newPhone) => {
    setMobileNumber(newPhone?.numberValue);
    formik.setFieldValue("country_code", newPhone?.countryCallingCode);
    formik.setFieldValue("mobile_number", newPhone?.nationalNumber);
  };

  const handleOfficeNoValueChange = (newOfficeNo) => {
    setOfficeNumber(newOfficeNo?.numberValue);
    formik.setFieldValue("office_number", newOfficeNo?.nationalNumber);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <TableContainer>
        <Box sx={{ padding: "20px" }}>
          <MDBox fontSize="20px" fontWeight="600" color={sideNav.textColor}>
            About us
          </MDBox>
          <Box component="form" role="form" onSubmit={formik.handleSubmit}>
            <Grid container spacing={2} rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
              {/* Logo */}
              <Grid item xs={12} md={12} sx={{ paddingTop: "20px !important" }}>
                <ImageUploader
                  img={profileImage.url}
                  folderName={"/informations"}
                  name={"logo"}
                  fileName={"logo"}
                  formik={formik}
                  label="Image"
                  sx={{
                    width: "100px",
                    height: "100px",
                    objectFit: "cover",
                    borderRadius: "50%",
                    marginBottom: "20px",
                  }}
                  required
                />
                {formik.touched.logo && formik.errors.logo && (
                  <Box sx={{ fontSize: "12px", color: "red", marginLeft: "5px" }}>
                    {formik.errors.logo}
                  </Box>
                )}
              </Grid>

              {/* Name */}
              <Grid item xs={12} md={6} mt={1}>
                <MDInput
                  type="text"
                  name="name"
                  value={formik.values.name}
                  onChange={(event) => {
                    formik.handleChange(event);
                  }}
                  onBlur={formik.handleBlur}
                  label="Name"
                  sx={{ width: "100%" }}
                  variant="standard"
                  required
                />
                {formik.touched.name && formik.errors.name && (
                  <Box sx={{ fontSize: "12px", color: "red", marginTop: "5px" }}>
                    {formik.errors.name}
                  </Box>
                )}
              </Grid>

              {/* Email */}
              <Grid item xs={12} md={6} mt={1}>
                <MDInput
                  type="text"
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Email"
                  sx={{ width: "100%" }}
                  variant="standard"
                  required
                />
                {formik.touched.email && formik.errors.email && (
                  <Box sx={{ fontSize: "12px", color: "red", marginTop: "5px" }}>
                    {formik.errors.email}
                  </Box>
                )}
              </Grid>

              {/* Mobile No. */}
              <Grid item xs={12} md={6} sx={{ paddingTop: "10px !important" }}>
                <MDTelInput
                  name="mobile_number"
                  value={mobileNumber}
                  handleChange={(event) => handlePhoneValueChange(event)}
                  onBlur={formik.handleBlur}
                  label="Mobile No"
                  sx={{ width: "100%" }}
                  variant="standard"
                  required
                />
                {formik.touched.mobile_number && formik.errors.mobile_number && (
                  <Box sx={{ fontSize: "12px", color: "red", marginTop: "5px" }}>
                    {formik.errors.mobile_number}
                  </Box>
                )}
              </Grid>

              {/* Office No. */}
              <Grid item xs={12} md={6} sx={{ paddingTop: "10px !important" }}>
                <MDTelInput
                  name="office_number"
                  value={officeNumber}
                  handleChange={(event) => handleOfficeNoValueChange(event)}
                  onBlur={formik.handleBlur}
                  label="Office No"
                  sx={{ width: "100%" }}
                  variant="standard"
                />
              </Grid>

              {/* Address */}
              <Grid item xs={12} md={12} sx={{ paddingTop: "20px !important" }}>
                <MDTextField
                  type="text"
                  name="address"
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  sx={{ width: "100%" }}
                  label="Address"
                  variant="standard"
                  required
                  multiline
                  rows={4}
                />
                {formik.touched.address && formik.errors.address && (
                  <Box sx={{ fontSize: "12px", color: "red", marginTop: "5px" }}>
                    {formik.errors.address}
                  </Box>
                )}
              </Grid>
              <Grid
                item
                xs={12}
                md={12}
                sx={{
                  paddingTop: "30px !important",
                }}
              >
                <MDGoogleMap
                  selectedPosition={{ lat: formik.values.latitude, lng: formik.values.longitude }}
                  formik={formik}
                  formikKeyName="address"
                />
              </Grid>
              {/* Description */}
              <Grid item xs={12} md={12} sx={{ paddingTop: "20px !important" }}>
                <MDBox sx={{ fontSize: "16px", fontWeight: "400", marginBottom: "5px", marginTop: "10px" }}>
                  Description
                </MDBox>
                <TinyTextEditor
                  setContent={(value) => formik.setFieldValue("description", value)}
                  content={formik.values.description}
                  onBlur={formik.handleBlur}
                  folderName={"/informations"}
                />
              </Grid>

              <Grid item xs={12} md={12} sx={{ paddingTop: "20px !important" }}>
                <MDBox sx={{ fontSize: "16px", fontWeight: "400", marginBottom: "5px", marginTop: "10px" }}>
                  Privacy Policy
                </MDBox>
                <TinyTextEditor
                  setContent={(value) => formik.setFieldValue("privacy_policy", value)}
                  content={formik.values.privacy_policy}
                  onBlur={formik.handleBlur}
                  folderName={"/informations"}
                />
              </Grid>

              <Grid item xs={12} md={12} sx={{ paddingTop: "20px !important" }}>
                <MDBox sx={{ fontSize: "16px", fontWeight: "400", marginBottom: "5px", marginTop: "10px" }}>
                  Terms and Conditions
                </MDBox>
                <TinyTextEditor
                  setContent={(value) => formik.setFieldValue("terms_and_condition", value)}
                  content={formik.values.terms_and_condition}
                  onBlur={formik.handleBlur}
                  folderName={"/informations"}
                />
              </Grid>

              {/* Instagram Link */}
              <Grid item xs={12} md={6} sx={{ paddingTop: "20px !important" }}>
                <MDInput
                  type="text"
                  name="instagram_link"
                  value={formik.values.instagram_link}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  sx={{ width: "100%" }}
                  label="Instagram Link"
                  variant="standard"
                />
              </Grid>

              {/* Youtube Link */}
              <Grid item xs={12} md={6} sx={{ paddingTop: "20px !important" }}>
                <MDInput
                  type="text"
                  name="youtube_link"
                  value={formik.values.youtube_link}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  sx={{ width: "100%" }}
                  label="Youtube Link"
                  variant="standard"
                />
              </Grid>

              {/* Facebook Link */}
              <Grid item xs={12} md={6} sx={{ paddingTop: "20px !important" }}>
                <MDInput
                  type="text"
                  name="facebook_link"
                  value={formik.values.facebook_link}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  sx={{ width: "100%" }}
                  label="Facebook Link"
                  variant="standard"
                />
              </Grid>

              {/* Twitter Link */}
              <Grid item xs={12} md={6} sx={{ paddingTop: "20px !important" }}>
                <MDInput
                  type="text"
                  name="twitter_link"
                  value={formik.values.twitter_link}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  sx={{ width: "100%" }}
                  label="Twitter Link"
                  variant="standard"
                />
              </Grid>
            </Grid>
            <Box sx={{ width: "100%", textAlign: "center", marginTop: "40px" }}>
              <SubmitButton btnText={"Update"} type="submit" />
            </Box>
          </Box>
        </Box>
        {/* </Box> */}
      </TableContainer>
    </DashboardLayout>
  );
};

export default AboutUs;
